import React from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Badge } from "./badge";

function FilledCheck({ className }) {
  return (
    <span
      className={cn(
        "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground",
        className,
      )}
      aria-hidden
    >
      <Check className="size-3.5" strokeWidth={2.25} />
    </span>
  );
}

/**
 * Single small bento cell (Starter / Teams / Enterprise in upstream template).
 */
function PricingCard({
  titleBadge,
  priceLabel,
  priceSuffix = "/month",
  features,
  cta = "Get started",
  className,
  ctaTo = "/start-project",
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md border border-foreground/10 bg-background backdrop-blur supports-[backdrop-filter]:bg-background/10",
        className,
      )}
    >
      <div className="flex items-center gap-3 p-4">
        <Badge variant="secondary">{titleBadge}</Badge>
        <div className="ml-auto">
          <Button variant="outline" asChild size="sm">
            <Link to={ctaTo}>{cta}</Link>
          </Button>
        </div>
      </div>

      <div className="flex items-end gap-2 px-4 py-2">
        <span className="font-mono text-5xl font-semibold tracking-tight">
          {priceLabel}
        </span>
        {String(priceLabel).toLowerCase() !== "free" &&
          priceSuffix !== undefined &&
          priceSuffix !== "" && (
          <span className="text-sm text-muted-foreground">{priceSuffix}</span>
        )}
      </div>

      <ul className="grid gap-4 p-4 text-sm text-muted-foreground">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <FilledCheck />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Responsive bento grid pricing — Tailwind v3–safe gradients (repo is CRA + JSX, not Next).
 *
 * Pass copy from `Home` via props; headings use optional `title` / `subtitle` for demo-style wrapper.
 */
export function BentoPricing({
  title,
  subtitle,
  featuredBadge,
  recommendedLabel,
  featuredPrice,
  featuredPriceSuffix = "/month",
  featuredFeatures,
  featuredCta = "Get started",
  stackedCards,
  sectionClassName,
  ctaTo = "/start-project",
}) {
  return (
    <section className={cn("relative px-4 py-16 md:py-24", sectionClassName)}>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_85%_60%_at_50%_0%,hsl(var(--foreground)/0.08),transparent_65%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(hsl(var(--foreground)/0.07)_1px,transparent_1px)] opacity-70 [background-size:12px_12px]"
      />

      {(title || subtitle) && (
        <div className="mx-auto mb-10 max-w-2xl text-center">
          {title ? (
            <h2 className="text-balance text-4xl font-extrabold tracking-tight lg:text-5xl">
              {title}
            </h2>
          ) : null}
          {subtitle ? (
            <p className="mt-4 whitespace-pre-line text-sm text-muted-foreground md:text-base">
              {subtitle}
            </p>
          ) : null}
        </div>
      )}

      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-8">
        {/* Featured tile */}
        <div
          className={cn(
            "relative col-span-full w-full overflow-hidden rounded-md border border-foreground/10 bg-background backdrop-blur supports-[backdrop-filter]:bg-background/10 lg:col-span-5",
          )}
        >
          <div className="pointer-events-none absolute top-0 left-1/2 -mt-2 -ml-20 h-full w-full [mask-image:linear-gradient(white,transparent)]">
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/5 to-foreground/[0.07] [mask-image:radial-gradient(farthest-side_at_top,white,transparent)]">
              <div
                aria-hidden
                className="absolute inset-0 size-full mix-blend-overlay [background-image:linear-gradient(to_right,hsl(var(--foreground)/0.06)_1px,transparent_1px)] bg-[length:24px_100%]"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <Badge variant="secondary">{featuredBadge}</Badge>
            {recommendedLabel ? (
              <Badge variant="outline" className="hidden lg:inline-flex">
                <Sparkles className="size-3 ltr:me-1 rtl:ms-1" aria-hidden />{" "}
                {recommendedLabel}
              </Badge>
            ) : null}
            <div className="ml-auto shrink-0">
              <Button asChild size="sm">
                <Link to={ctaTo}>{featuredCta}</Link>
              </Button>
            </div>
          </div>
          <div className="flex flex-col p-4 lg:flex-row">
            <div className="pb-4 lg:w-[30%]">
              <span className="font-mono text-5xl font-semibold tracking-tight">
                {featuredPrice}
              </span>
              {featuredPriceSuffix !== undefined &&
                featuredPriceSuffix !== "" ? (
                  <span className="text-sm text-muted-foreground">
                    {featuredPriceSuffix}
                  </span>
                ) : null}
            </div>
            <ul className="grid gap-4 text-sm text-muted-foreground lg:w-[70%]">
              {featuredFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <FilledCheck />
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {(stackedCards || [])[0] ? (
          <PricingCard
            key={stackedCards[0].titleBadge ?? "tier-1"}
            titleBadge={stackedCards[0].titleBadge}
            priceLabel={stackedCards[0].priceLabel}
            priceSuffix={stackedCards[0].priceSuffix}
            features={stackedCards[0].features}
            cta={stackedCards[0].cta}
            ctaTo={stackedCards[0].ctaTo ?? ctaTo}
            className="lg:col-span-3"
          />
        ) : null}

        {(stackedCards || [])[1] ? (
          <PricingCard
            key={stackedCards[1].titleBadge ?? "tier-2"}
            titleBadge={stackedCards[1].titleBadge}
            priceLabel={stackedCards[1].priceLabel}
            priceSuffix={stackedCards[1].priceSuffix}
            features={stackedCards[1].features}
            cta={stackedCards[1].cta}
            ctaTo={stackedCards[1].ctaTo ?? ctaTo}
            className="lg:col-span-4"
          />
        ) : null}

        {(stackedCards || [])[2] ? (
          <PricingCard
            key={stackedCards[2].titleBadge ?? "tier-3"}
            titleBadge={stackedCards[2].titleBadge}
            priceLabel={stackedCards[2].priceLabel}
            priceSuffix={stackedCards[2].priceSuffix}
            features={stackedCards[2].features}
            cta={stackedCards[2].cta}
            ctaTo={stackedCards[2].ctaTo ?? ctaTo}
            className="lg:col-span-4"
          />
        ) : null}
      </div>
    </section>
  );
}

export default BentoPricing;
